import { SsrDetails } from '@tff/types/tff/tfa-amend-ancillary-service';

const removeSsrFromOriginalSsrs = (includedSsrsForPax: SsrDetails[], ssr: SsrDetails) => {
  if (
    includedSsrsForPax?.find(
      (includedSsr: SsrDetails) => includedSsr.code === ssr.code && includedSsr.segmentOnd === ssr.segmentOnd,
    )
  ) {
    return {
      ...ssr,
      code: ssr.code + '-remove',
    };
  }
};

// Case when baggage was to be removed from original ssrs
const wasNewBaggageToBeRemoved = (newBaggage: SsrDetails, ssr: SsrDetails) => {
  return newBaggage.code === ssr.code.replace('-remove', '');
};

const swapBaggage = (
  paxId: string,
  newBaggage: SsrDetails,
  setSsrsToAdd: (arg0: (prevState: any) => any) => void,
  includedSsrs: { [x: string]: any[] },
) => {
  setSsrsToAdd(prevState => {
    const newSsrs = prevState[paxId].reduce((acc: SsrDetails[], ssr: SsrDetails) => {
      if (ssr.segmentOnd === newBaggage.segmentOnd) {
        //Baggage case ('BA')
        if (ssr.code.startsWith('BA')) {
          if (ssr.code.includes('remove')) {
            if (wasNewBaggageToBeRemoved(newBaggage, ssr)) {
              // ssr is removed from changes as it will be added in line 46
              return acc;
            } else {
              acc.push(ssr);
            }
          }
          // Replace old baggage to new one (e.g. previously was BA25, now will be BA32)
          else {
            acc.push(newBaggage);
          }
          const ssrToRemove = removeSsrFromOriginalSsrs(includedSsrs[paxId], ssr);
          if (ssrToRemove) {
            acc.push(ssrToRemove);
          }
        }
        //Additional Luggage case ('BX')
        else {
          // Remove additional baggage in case user chooses Hand luggage only ('BA0')
          if (newBaggage.code === 'BA0' && ssr.code.startsWith('BX')) {
            const ssrToRemove = removeSsrFromOriginalSsrs(includedSsrs[paxId], ssr);
            if (ssrToRemove) {
              acc.push(ssrToRemove);
            }
          } else {
            acc.push(ssr);
          }
        }
      } else {
        acc.push(ssr);
      }
      return acc;
    }, []);
    return {
      ...prevState,
      [paxId]: newSsrs,
    };
  });
};

const isBaggageIncluded = (value: string | undefined) => {
  return value && /(BI[0-9][0-9])/.test(value);
};

export const addSsrsToAdd = (paxId: string, ssrToAdd: SsrDetails, setSsrsToAdd, ssrsToAdd, includedSsrs) => {
  //Included baggage included case ('BI')
  if (isBaggageIncluded(ssrToAdd.code)) {
    // Remove all other BA baggage
    setSsrsToAdd(prevState => {
      const newSsrs = prevState[paxId].reduce((acc: SsrDetails[], ssr: SsrDetails) => {
        if (ssr.segmentOnd === ssrToAdd.segmentOnd && ssr.code.startsWith('BA')) {
          const ssrToRemove = removeSsrFromOriginalSsrs(includedSsrs[paxId], ssr);
          if (ssrToRemove) {
            // Remove BA from original ssrs
            acc.push(ssrToRemove);
          } else {
            // Remove BA from current ssrs
            return acc;
          }
        } else {
          acc.push(ssr);
        }
        return acc;
      }, []);
      return {
        ...prevState,
        [paxId]: newSsrs,
      };
    });
    return;
  }

  //Baggage case ('BA')
  if (ssrToAdd.code.startsWith('BA')) {
    // Confirm with Tolga if existingBaggage can be removed has it seems that it is always true - we'll always have a "BA" in ssrsToAdd
    const existingBaggage = ssrsToAdd[paxId]?.find(ssr => {
      return ssr.code.startsWith('BA') && ssr.segmentOnd === ssrToAdd.segmentOnd;
    });
    if (existingBaggage) {
      swapBaggage(paxId, ssrToAdd, setSsrsToAdd, includedSsrs);
      return;
    }
  }

  setSsrsToAdd(prevState => {
    const ssrWithSameCode = prevState[paxId].filter(
      ssr => ssr.code === ssrToAdd.code && ssr.segmentOnd === ssrToAdd.segmentOnd,
    );

    if (ssrWithSameCode.length > 0) {
      return {
        ...prevState,
        [paxId]: prevState[paxId].map(ssr => {
          if (ssr.code === ssrToAdd.code && ssr.segmentOnd === ssrToAdd.segmentOnd) {
            return { ...ssr, amount: ssr.amount + 1, tuiRelated: true };
          }
          return ssr;
        }),
      };
    } else {
      const ssrToRemoveWithSameCode = prevState[paxId].filter(
        ssr =>
          ssr.code.includes('remove') &&
          ssr.code.replace('-remove', '') === ssrToAdd.code &&
          ssr.segmentOnd === ssrToAdd.segmentOnd,
      );

      if (ssrToRemoveWithSameCode.length > 0) {
        return {
          ...prevState,
          [paxId]: prevState[paxId].map(ssr => {
            if (
              ssr.code.includes('remove') &&
              ssr.code.replace('-remove', '') === ssrToAdd.code &&
              ssr.segmentOnd === ssrToAdd.segmentOnd
            ) {
              return { ...ssr, code: ssrToAdd.code, amount: ssrToAdd.amount, tuiRelated: true };
            }
            return ssr;
          }),
        };
      }

      return {
        ...prevState,
        [paxId]: [...(prevState[paxId] || []), ssrToAdd],
      };
    }
  });
};
